import momentTimezone from 'moment-timezone';
import { useFilterContext } from './FilterContext';
import { FilterCondition } from 'framework/types';
import moment, { Moment } from 'moment';
import { DEFAULT_TIMEZONE, useDateTimeService, DATE_FORMAT, DEFAULT_FORMAT } from '../../utils/hooks/useDateService';
import { useEffect, useState } from 'react';
import useFilterValidation from './useFilterValidation';

export const FILTER_DATE_ERROR = 'Please choose valid date in past';

const useSingleDateFilter = (
  dateFormat: string,
  dateTimeFormat: string,
  canBeInFuture: boolean,
  source: string,
  condition: FilterCondition,
) => {
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const { getTimezone, getDateInCurrentTimezone } = useDateTimeService();
  const { getFilterErrors, setFilterErrors } = useFilterContext();

  const { maxDate, minDate } = useFilterValidation(`${source}.${condition}`);

  useEffect(() => {
    (async () => {
      if (getTimezone) {
        const userTimezone = await getTimezone();
        setTimezone(userTimezone as string);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMaxDateDefault = () => {
    return canBeInFuture ? null : momentTimezone.tz(moment(), timezone);
  };

  const validateAndShowErrorMessages = (inputName: FilterCondition, inputValue: string) => {
    const date = momentTimezone.tz(inputValue, dateFormat, timezone);
    const isValid = !inputValue || (date.isValid() && (canBeInFuture || date.isSameOrBefore(getMaxDate())));
    const showErrors = !isValid;
    if (showErrors) {
      setFilterErrors(source, inputName, [FILTER_DATE_ERROR]);
    } else {
      setFilterErrors(source, inputName, []);
    }
    return showErrors;
  };

  const fromApiToFrontFormat = (apiTextDate: string) => {
    const isValidDateTimeFormat = momentTimezone(apiTextDate, dateTimeFormat, true).isValid();
    const isValidDateFormat = momentTimezone(apiTextDate, 'YYYY-MM-DD', true).isValid();

    if (isValidDateTimeFormat) {
      return getDateInCurrentTimezone(apiTextDate).format(dateFormat);
    }

    if (isValidDateFormat) {
      return momentTimezone(apiTextDate).format(dateFormat);
    }

    return apiTextDate;
  };

  const getErrors = (key: FilterCondition) => {
    return getFilterErrors(source, key);
  };

  const localDate = (date: string) => momentTimezone.tz(date, timezone);

  const formatDateFormValidationSchema = (date?: Nullable<string | Moment>) => {
    return !date || typeof date === 'string'
      ? ''
      : getDateInCurrentTimezone(date.format(dateTimeFormat)).format(DEFAULT_FORMAT);
  };

  const formatDateForAPI = (dateText: string) => {
    return moment(dateText, dateFormat).format(DATE_FORMAT.API_ONLY_DATE);
  };

  const getMaxDate = () => formatDateFormValidationSchema(maxDate()) || getMaxDateDefault();

  const getMinDate = () => formatDateFormValidationSchema(minDate());

  return {
    validateAndShowErrorMessages,
    fromApiToFrontFormat,
    getErrors,
    getMaxDate,
    getMinDate,
    localDate,
    formatDateForAPI,
  };
};

export default useSingleDateFilter;
